import { useState } from "react";
import { Clock, Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

interface ReservationTimesEditorProps {
  reservationId: string;
  propertyId: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  /** Current guest_count from the reservation (may be null when iCal didn't supply one). */
  guestCount?: number | null;
}

const TIME_SUGGESTIONS = [
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "Flexible",
  "Unknown",
];

export const ReservationTimesEditor = ({
  reservationId,
  propertyId,
  checkInTime,
  checkOutTime,
  guestCount,
}: ReservationTimesEditorProps) => {
  const [localCheckIn, setLocalCheckIn] = useState(checkInTime || "");
  const [localCheckOut, setLocalCheckOut] = useState(checkOutTime || "");
  const [localGuestCount, setLocalGuestCount] = useState<string>(
    guestCount != null ? String(guestCount) : ""
  );
  const [saving, setSaving] = useState(false);
  const [showCheckInSuggestions, setShowCheckInSuggestions] = useState(false);
  const [showCheckOutSuggestions, setShowCheckOutSuggestions] = useState(false);
  const queryClient = useQueryClient();

  const initialGuestCountStr = guestCount != null ? String(guestCount) : "";
  const hasChanges =
    localCheckIn !== (checkInTime || "") ||
    localCheckOut !== (checkOutTime || "") ||
    localGuestCount.trim() !== initialGuestCountStr;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Parse guest count manually so we can explicitly clear (null) vs set 0 vs set N.
      // Reject negative / non-integer values, but allow empty string -> null.
      let nextGuestCount: number | null | undefined = undefined;
      const trimmed = localGuestCount.trim();
      if (trimmed !== initialGuestCountStr) {
        if (trimmed === "") {
          nextGuestCount = null;
        } else {
          const parsed = parseInt(trimmed, 10);
          if (!Number.isFinite(parsed) || parsed < 0) {
            toast({
              title: "Invalid guest count",
              description: "Enter a whole number (or leave blank to clear).",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }
          nextGuestCount = parsed;
        }
      }

      const { error } = await supabase
        .from("reservations")
        .update({
          check_in_time: localCheckIn || null,
          check_out_time: localCheckOut || null,
          ...(nextGuestCount !== undefined && { guest_count: nextGuestCount }),
        })
        .eq("id", reservationId);

      if (error) throw error;

      toast({ title: "Saved", description: "Reservation details updated" });
      queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", propertyId] });
    } catch (error) {
      console.error("Error saving reservation:", error);
      toast({ title: "Error", description: "Failed to save reservation", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const selectSuggestion = (value: string, type: "in" | "out") => {
    if (type === "in") {
      setLocalCheckIn(value);
      setShowCheckInSuggestions(false);
    } else {
      setLocalCheckOut(value);
      setShowCheckOutSuggestions(false);
    }
  };

  return (
    <div className="space-y-3 py-3 px-1">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Clock className="w-4 h-4" />
        <span>Check-In / Check-Out Times</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Check-In Time */}
        <div className="space-y-1.5">
          <Label htmlFor="check-in-time" className="text-xs text-muted-foreground">
            Check-In Time
          </Label>
          <div className="relative">
            <Input
              id="check-in-time"
              value={localCheckIn}
              onChange={(e) => setLocalCheckIn(e.target.value)}
              onClick={() => setShowCheckInSuggestions(true)}
              onBlur={() => setTimeout(() => setShowCheckInSuggestions(false), 200)}
              placeholder="e.g., 3:00 PM"
              className="h-9 text-sm"
              autoComplete="off"
            />
            {showCheckInSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                {TIME_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    onMouseDown={() => selectSuggestion(suggestion, "in")}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Check-Out Time */}
        <div className="space-y-1.5">
          <Label htmlFor="check-out-time" className="text-xs text-muted-foreground">
            Check-Out Time
          </Label>
          <div className="relative">
            <Input
              id="check-out-time"
              value={localCheckOut}
              onChange={(e) => setLocalCheckOut(e.target.value)}
              onClick={() => setShowCheckOutSuggestions(true)}
              onBlur={() => setTimeout(() => setShowCheckOutSuggestions(false), 200)}
              placeholder="e.g., 11:00 AM"
              className="h-9 text-sm"
              autoComplete="off"
            />
            {showCheckOutSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                {TIME_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    onMouseDown={() => selectSuggestion(suggestion, "out")}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Guest count — entered manually since iCal feeds rarely include it. */}
      <div className="space-y-1.5">
        <Label
          htmlFor={`guest-count-${reservationId}`}
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Users className="h-3 w-3" />
          Guests (optional)
          {guestCount == null && (
            <span className="text-amber-700 dark:text-amber-300">— not provided by source</span>
          )}
        </Label>
        <Input
          id={`guest-count-${reservationId}`}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={localGuestCount}
          onChange={(e) => setLocalGuestCount(e.target.value)}
          placeholder="e.g. 4"
          className="h-9 max-w-[10rem] text-sm"
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Used to size linens, amenities, and cleaning recommendations. Stravo does not scrape guest emails.
        </p>
      </div>

      {hasChanges && (
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto"
        >
          <Check className="w-4 h-4 mr-1" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      )}
    </div>
  );
};
